import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UserDto, UpdateUserInput } from '@cutlyft/zod-schemas';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { useAuthStore } from '@/stores/auth.store';

interface UpdateUserApiResponse {
  success: boolean;
  data: UserDto;
}

interface UpdateUserMutationInput {
  id: number;
  input: UpdateUserInput;
}

/**
 * Mutation hook for updating a user's details (name or role).
 * Calls PATCH /users/:id.
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((state) => state.user);
  const setAuth = useAuthStore((state) => state.setAuth);
  const tenant = useAuthStore((state) => state.tenant);
  const accessToken = useAuthStore((state) => state.accessToken);

  return useMutation({
    mutationFn: async ({ id, input }: UpdateUserMutationInput) => {
      const response = await apiClient.patch<UpdateUserApiResponse>(`/users/${id}`, input);
      return response.data.data;
    },
    onSuccess: (data) => {
      // Invalidate users list and single user lookup queries
      queryClient.invalidateQueries({ queryKey: queryKeys.users.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.byId(data.id) });

      // If updating our own profile, update the local auth store state
      if (currentUser && data.id === currentUser.id && tenant && accessToken) {
        setAuth(
          {
            id: data.id,
            email: data.email,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            roleCode: data.roleCode,
            emailVerified: data.emailVerified,
          },
          tenant,
          accessToken
        );
      }
    },
  });
}
